import React, { useCallback, useEffect, useState } from "react";
import dateFormat from "dateformat";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import LoadingAnimation from "components/Animations/loading";
import { getDashboardAuditLog } from "network/ApiAxios";

const actionColor = (action) => ({
  CREATE: "primary",
  UPDATE: "secondary",
  DELETE: "default",
}[action] || "default");

const AuditLog = () => {
  const [data, setData] = useState({ records: [], entityTypes: [], actors: [], totalPages: 0, totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ actor: "", action: "", entityType: "", search: "", from: "", to: "", page: 0 });

  const load = useCallback(async (nextFilters) => {
    setLoading(true);
    try {
      const response = await getDashboardAuditLog(nextFilters);
      if (response.data && response.data.result && response.data.result.isSuccess) {
        setData(response.data.data || { records: [], entityTypes: [], actors: [], totalPages: 0, totalElements: 0 });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Filters are intentionally immediate. A small delay while typing avoids a
  // network request for every individual character in the search field.
  useEffect(() => {
    const delay = filters.search ? 300 : 0;
    const timer = setTimeout(() => load(filters), delay);
    return () => clearTimeout(timer);
  }, [filters, load]);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value, page: 0 }));
  const clearFilters = () => {
    const clean = { actor: "", action: "", entityType: "", search: "", from: "", to: "", page: 0 };
    setFilters(clean);
  };
  const changePage = (page) => {
    const next = { ...filters, page };
    setFilters(next);
  };

  return (
    <Container maxWidth={false} component={Box} marginTop="-4.5rem" paddingBottom="3rem">
      <Card>
        <CardHeader
          title="Activity Log"
          subheader="Successful changes made by dashboard users. Records are scoped to the selected country."
        />
        <CardContent>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Name, section, record, or activity details"
                InputLabelProps={{ shrink: true }}
                value={filters.search}
                onChange={(e) => setFilter("search", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel shrink>Who</InputLabel>
                <Select displayEmpty value={filters.actor} renderValue={(value) => value || "All users"} onChange={(e) => setFilter("actor", e.target.value)}>
                  <MenuItem value="">All users</MenuItem>
                  {(data.actors || []).map((actor) => <MenuItem key={actor} value={actor}>{actor}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel shrink>Action</InputLabel>
                <Select displayEmpty value={filters.action} renderValue={(value) => value || "All actions"} onChange={(e) => setFilter("action", e.target.value)}>
                  <MenuItem value="">All actions</MenuItem>
                  <MenuItem value="CREATE">Added</MenuItem>
                  <MenuItem value="UPDATE">Edited</MenuItem>
                  <MenuItem value="DELETE">Deleted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Section</InputLabel>
                <Select displayEmpty value={filters.entityType} renderValue={(value) => value || "All sections"} onChange={(e) => setFilter("entityType", e.target.value)}>
                  <MenuItem value="">All sections</MenuItem>
                  {(data.entityTypes || []).map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="From" type="date" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => setFilter("from", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="To" type="date" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => setFilter("to", e.target.value)} />
            </Grid>
          </Grid>
          <Box marginTop={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">{data.totalElements || 0} entries</Typography>
            <Button size="small" onClick={clearFilters}>Clear filters</Button>
          </Box>

          {loading ? <LoadingAnimation /> : (
            <Box marginTop={2} overflow="auto">
              <table className="table align-items-center table-flush">
                <thead className="thead-light">
                  <tr><th>When</th><th>Who</th><th>Action</th><th>Section</th><th>Record</th><th>Details</th></tr>
                </thead>
                <tbody>
                  {(data.records || []).map((record) => (
                    <tr key={record.id}>
                      <td>{record.createdAt ? dateFormat(new Date(record.createdAt), "dd mmm yyyy, h:MM TT") : "—"}</td>
                      <td>{record.actorName}</td>
                      <td><Chip size="small" color={actionColor(record.action)} label={record.action === "CREATE" ? "Added" : record.action === "DELETE" ? "Deleted" : "Edited"} /></td>
                      <td>{record.entityType}</td>
                      <td>{record.entityLabel || record.entityId || "—"}</td>
                      <td>{record.summary}</td>
                    </tr>
                  ))}
                  {!data.records || data.records.length === 0 ? <tr><td colSpan="6"><Typography align="center" color="textSecondary">No activity matches these filters.</Typography></td></tr> : null}
                </tbody>
              </table>
            </Box>
          )}
          <Box display="flex" justifyContent="flex-end" alignItems="center" marginTop={2} gridGap="0.75rem">
            <Button disabled={loading || filters.page <= 0} onClick={() => changePage(filters.page - 1)}>Previous</Button>
            <Typography variant="body2">Page {(filters.page || 0) + 1} of {Math.max(data.totalPages || 0, 1)}</Typography>
            <Button disabled={loading || (filters.page || 0) >= (data.totalPages || 1) - 1} onClick={() => changePage((filters.page || 0) + 1)}>Next</Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AuditLog;
